import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEFAULT_FLASHCARDS,
  type ChatBroadcastMessage,
  type ChatItem,
  type ChatReplyRef,
  type FlashCard,
  type FlashViewState,
  type ModerationAction,
  type PollState,
  type VoteState,
  type RoomMedia,
  type RoomMode,
  type RoomUser,
  type ServerMessage,
} from "@yay/shared";
import {
  config,
  getRoomName,
  getSessionId,
  loadIdentity,
  saveIdentity,
  wsUrlForRoom,
} from "../config";
import { YayConnection, type ConnectionStatus } from "../lib/connection";

function mediaEqual(a: RoomMedia, b: RoomMedia): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "youtube" && b.kind === "youtube") {
    if (a.youtubeId !== b.youtubeId) return false;
    const ap = a.playback;
    const bp = b.playback;
    if (!ap && !bp) return true;
    if (!ap || !bp) return false;
    return (
      ap.at === bp.at &&
      ap.playing === bp.playing &&
      ap.positionSec === bp.positionSec
    );
  }
  return true;
}

/** Same YouTube video with no flash/mode/view churn — skip full room_state apply. */
function isYoutubePlaybackOnlyRoomState(
  prev: RoomMedia,
  next: RoomMedia,
  prevMode: RoomMode,
  nextMode: RoomMode,
  prevCards: FlashCard[],
  nextCards: FlashCard[],
  prevViews: Record<string, FlashViewState>,
  nextViews: Record<string, FlashViewState>,
  prevFlashLock: boolean,
  nextFlashLock: boolean,
  prevOwnerId: string | null,
  nextOwnerId: string | null,
  prevOwnerPro: boolean,
  nextOwnerPro: boolean,
  prevMutedIds: string[],
  nextMutedIds: string[],
  prevBanned: Array<{ id: string; username: string }>,
  nextBanned: Array<{ id: string; username: string }>,
): boolean {
  if (prevMode !== nextMode) return false;
  if (!flashCardsEqual(prevCards, nextCards)) return false;
  if (!flashViewsEqual(prevViews, nextViews)) return false;
  if (prevFlashLock !== nextFlashLock) return false;
  if (prev.kind !== "youtube" || next.kind !== "youtube") return false;
  if (prev.youtubeId !== next.youtubeId) return false;
  // Moderation/ownership state must NOT ride the playback-only shortcut —
  // a ban/unban/mute/transfer broadcast in a YouTube room would otherwise
  // be skipped and leave the client's picker/labels/isOwner stale.
  if ((prevOwnerId ?? null) !== (nextOwnerId ?? null)) return false;
  if (prevOwnerPro !== nextOwnerPro) return false;
  if (!stringArrEqual(prevMutedIds, nextMutedIds)) return false;
  if (!bannedUsersEqual(prevBanned, nextBanned)) return false;
  return true;
}

function stringArrEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function bannedUsersEqual(
  a: Array<{ id: string; username: string }>,
  b: Array<{ id: string; username: string }>,
): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.id !== b[i]!.id || a[i]!.username !== b[i]!.username) {
      return false;
    }
  }
  return true;
}

function normalizeViews(
  raw: unknown,
): Record<string, FlashViewState> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, FlashViewState> = {};
  for (const [userId, value] of Object.entries(
    raw as Record<string, unknown>,
  )) {
    if (!value || typeof value !== "object") continue;
    const v = value as Partial<FlashViewState>;
    out[userId] = {
      cardIndex: typeof v.cardIndex === "number" ? v.cardIndex : 0,
      flipped: !!v.flipped,
      followingUserId:
        typeof v.followingUserId === "string" ? v.followingUserId : null,
    };
  }
  return out;
}

function flashViewsEqual(
  a: Record<string, FlashViewState>,
  b: Record<string, FlashViewState>,
): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    const left = a[key];
    const right = b[key];
    if (!left || !right) return false;
    if (
      left.cardIndex !== right.cardIndex ||
      left.flipped !== right.flipped ||
      left.followingUserId !== right.followingUserId
    ) {
      return false;
    }
  }
  return true;
}

function usersShallowEqual(a: RoomUser[], b: RoomUser[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i]!;
    const right = b[i]!;
    if (
      left.id !== right.id ||
      left.username !== right.username ||
      left.color !== right.color ||
      left.avatarSeed !== right.avatarSeed
    ) {
      return false;
    }
  }
  return true;
}

function flashCardsEqual(a: FlashCard[], b: FlashCard[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i]!;
    const right = b[i]!;
    if (
      left.id !== right.id ||
      left.term !== right.term ||
      left.definition !== right.definition
    ) {
      return false;
    }
  }
  return true;
}

/* ── display types ──────────────────────────────────────────────── */

export type PendingChatStatus = "optimistic" | "sending" | "failed";

/** Optimistic local message waiting for server broadcast (or timed out). */
export interface PendingChatMessage {
  type: "pending";
  clientId: string;
  username: string;
  color: string;
  text: string;
  status: PendingChatStatus;
  sentAt: number;
  replyTo?: ChatReplyRef;
}

export type DisplayChatItem = ChatItem | PendingChatMessage;

/** Wall-clock ms of a display item — used to keep chat chronological. */
function messageTime(m: DisplayChatItem): number {
  if (m.type === "pending") return m.sentAt;
  return (m as { timestamp?: number }).timestamp ?? 0;
}

/** How long a chat can stay "sending" before we mark it failed. */
const CHAT_DELIVERY_TIMEOUT_MS = 12_000;
/** Show the sending indicator only after this long without an ack. */
const SHOW_SENDING_AFTER_MS = 800;
/** How long rename/reroll can spin before we surface failure. */
const PROFILE_SYNC_TIMEOUT_MS = 12_000;
/** Only show "Reconnecting…" after the outage has lingered this long. */
const DISCONNECT_NOTICE_MS = 4_000;
/** Show a subtle "Restoring session…" if not ready this long while connected. */
const SESSION_SYNC_NOTICE_MS = 1_500;

/* ── dev mock helpers ───────────────────────────────────────────── */

const MOCK_NAMES = [
  "alex", "sarah", "jordan", "maya", "ryan", "priya", "omar", "lena",
  "david", "sofia", "kaito", "noah", "emma", "liam", "zara", "ethan",
  "aria", "lucas", "mia", "james", "yara", "niko", "hanna", "tyler",
  "jade", "caleb", "nora", "elias", "ivy", "marcus", "ruby", "theo",
  "chloe", "adrian", "lily", "julian", "nina", "finn", "isla", "kai",
  "sasha", "andrei", "bella", "ravi", "mina", "viktor", "tara", "leo",
  "cora", "miguel", "amir", "fatima", "dante", "mei", "hugo", "inez",
  "jules", "anika", "seb", "clara", "mateo", "giselle", "otto", "rory",
  "tess", "bodhi", "flora", "milo", "saskia", "enzo", "nieve", "axel",
  "suri", "damon", "lana", "koji", "petra", "rey", "celeste", "ibra",
  "yvonne", "ren", "lucia", "taj", "kaia", "casey", "orson", "suki",
  "roman", "sage", "zayn", "elyse", "felix", "ellis", "greer", "wren",
  "marco", "fiadh", "kian", "arwen",
] as const;

const MOCK_COLORS = [
  "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7",
  "#DDA0DD", "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E9",
  "#F8C471", "#82E0AA",
];

const MOCK_MESSAGES = [
  "lol", "haha", "bro what", "this is fire", "yo", "anyone here?",
  "that was sick", "no way", "let's gooo", "hmm", "classic",
  "wait what", "LMAO", "nice", "bruh moment", "same", "real",
  "based", "fr fr", "yo this beat", "w take", "nah that's wild",
  "can't believe it", "peak content", "lets go", "ayy", "gg",
  "this is cinema", "the vibes", "watching from 🇧🇷", "hello",
  "late night crew", "morning", "good pick", "favorite part",
  "rewind that", "iconic", "slay", "period", "so true",
  "need this energy", "literally me", "🔥🔥🔥", "underrated",
  "deserved", "crying", "on repeat", "mood", "always",
  "omg yes", "valid", "smooth", "insane", "w setup",
];

function makeMockUser(i: number): RoomUser {
  const name = MOCK_NAMES[i] ?? `user${i}`;
  return {
    id: `mock-${i}`,
    username: name,
    color: MOCK_COLORS[i % MOCK_COLORS.length] ?? "#9147ff",
    avatarSeed: `mock-seed-${name}`,
  };
}

const MOCK_USERS: RoomUser[] = Array.from({ length: 100 }, (_, i) => makeMockUser(i));

function randomMockUser(): RoomUser {
  return MOCK_USERS[Math.floor(Math.random() * MOCK_USERS.length)]!;
}

function randomMockMessage(): ChatBroadcastMessage {
  const user = randomMockUser();
  const text = MOCK_MESSAGES[Math.floor(Math.random() * MOCK_MESSAGES.length)] ?? "lol";
  return {
    type: "chat",
    id: crypto.randomUUID(),
    username: user.username,
    color: user.color,
    text,
    timestamp: Date.now(),
    reactions: {},
  };
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

let mentionTitleTimer: ReturnType<typeof setTimeout> | null = null;
function flashMentionTitle(username: string): void {
  if (typeof document === "undefined") return;
  if (document.visibilityState === "visible") return;
  const prev = document.title;
  document.title = `@${username} — Yay`;
  if (mentionTitleTimer) clearTimeout(mentionTitleTimer);
  mentionTitleTimer = setTimeout(() => {
    document.title = prev;
    mentionTitleTimer = null;
  }, 4_000);
}

const MOCK_ENABLED =
  // Dev-only: fake users + chat stream for building the theater without a
  // server. Gated to DEV builds so ?mock can't be used against production.
  import.meta.env.DEV &&
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("mock");

/* ── hook ──────────────────────────────────────────────────────── */

export interface RoomState {
  status: ConnectionStatus;
  /** Server has confirmed our session; false while rejoining after idle/expiry. */
  sessionReady: boolean;
  me: RoomUser | null;
  users: RoomUser[];
  viewerCount: number;
  messages: DisplayChatItem[];
  lastError: string | null;
  reportOutcome:
    | { status: "pending" }
    | { status: "sent" }
    | { status: "failed" }
    | null;
  /** True while rename/reroll is waiting for server ack. */
  profileSyncing: boolean;
  roomName: string;
  mode: RoomMode;
  cards: FlashCard[];
  cardIndex: number;
  flipped: boolean;
  followingUserId: string | null;
  media: RoomMedia;
  /** Estimated client↔server clock offset (ms) for wall-clock-anchored sync. */
  clockOffsetMs: number;
  flashLock: boolean;
  flashSetLock: (locked: boolean) => boolean;
  /** Room owner's user id (null in the lobby / unowned rooms). */
  ownerId: string | null;
  /** Whether the current owner holds the Pro entitlement (authoritative
   * moderation) — lets the UI label owner actions honestly. */
  ownerPro: boolean;
  /** Banned members (id + username) — the /unban picker's target list. */
  bannedUsers: Array<{ id: string; username: string }>;
  /** Whether the current user is the room owner. */
  isOwner: boolean;
  /** User ids currently muted by the owner (mute is a toggle). */
  mutedIds: string[];
  /** Whether the current user is muted (chat blocked server-side). */
  muted: boolean;
  /** Active poll, or null when none. */
  poll: PollState | null;
  /** Active guest-moderation vote (kick/ban), or null when none. */
  vote: VoteState | null;
  /** Reason the current session was kicked/banned (terminal), or null. */
  kicked: string | null;
  /** Server is at capacity / under maintenance (cost gate) — terminal screen. */
  capacity: boolean;
  /** Re-check the gate and drop back to the join overlay when it opens. */
  clearCapacity: () => void;
  /** Join failed on a room password — stay on the overlay. */
  joinBlocked: string | null;
  clearJoinBlocked: () => void;
  /** Cast a vote in the active room vote (guests' non-authoritative moderation). */
  sendVoteCast: (voteId: string, approve: boolean) => boolean;
  join: (username: string, password?: string) => Promise<void>;
  sendChat: (text: string, replyToId?: string, replyHint?: ChatReplyRef) => boolean;
  retryChat: (clientId: string) => boolean;
  /** True while server rate-limit cooldown is active. */
  chatLocked: boolean;
  /** Wall-clock ms when chat unlocks; 0 when not locked. */
  rateLimitUntil: number;
  react: (messageId: string, emoji: string) => boolean;
  rename: (username: string) => boolean;
  reroll: () => boolean;
  leave: () => void;
  clearError: () => void;
  setMode: (mode: RoomMode) => boolean;
  flashFlip: () => boolean;
  flashNav: (delta: number) => boolean;
  flashAdd: (term: string, definition: string) => boolean;
  flashImport: (cards: Array<{ term: string; definition: string }>) => boolean;
  flashUpdate: (id: string, term: string, definition: string) => boolean;
  flashDelete: (id: string) => boolean;
  flashFollow: (targetUserId: string | null) => boolean;
  setMedia: (youtube: string) => boolean;
  syncMedia: (sync: { playing?: boolean; positionSec?: number }) => boolean;
  /** Report a user's behavior (moderation). */
  report: (targetUserId: string, reason: string) => boolean;
  sendCommand: (action: ModerationAction, targetIds: string[]) => boolean;
  sendWhisper: (targetUserId: string, text: string) => boolean;
  sendPollCreate: (title: string, options: string[]) => boolean;
  sendPollVote: (pollId: string, optionIndex: number) => boolean;
  sendPollClose: (pollId: string) => boolean;
}

export function useRoom(): RoomState {
  const connRef = useRef<YayConnection | null>(null);
  const usernameRef = useRef<string | null>(null);
  const meRef = useRef<RoomUser | null>(null);
  const profileTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionNoticeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disconnectNoticeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Snapshot of `me` before the last optimstic reroll, for rollback on error. */
  const prevMeRef = useRef<RoomUser | null>(null);
  const usersRef = useRef<RoomUser[]>([]);
  /** Fresh page load shouldn't resurrect leftover solo nerd. */
  const bootModeRef = useRef({ handled: false });
  const rateLimitUntilRef = useRef(0);
  /** Throttle presence-driven rejoins so a persistent mismatch can't loop. */
  const lastRejoinRef = useRef(0);

  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const statusRef = useRef<ConnectionStatus>("idle");
  statusRef.current = status;
  const [sessionReady, setSessionReady] = useState(false);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [ownerPro, setOwnerPro] = useState(false);
  const [bannedUsers, setBannedUsers] = useState<
    Array<{ id: string; username: string }>
  >([]);
  const [mutedIds, setMutedIds] = useState<string[]>([]);
  const [muted, setMuted] = useState(false);
  const [poll, setPoll] = useState<PollState | null>(null);
  const [vote, setVote] = useState<VoteState | null>(null);
  const [kicked, setKicked] = useState<string | null>(null);
  const [capacity, setCapacity] = useState(false);
  const [joinBlocked, setJoinBlocked] = useState<string | null>(null);
  const [me, setMe] = useState<RoomUser | null>(null);
  const [users, setUsers] = useState<RoomUser[]>(MOCK_ENABLED ? MOCK_USERS : []);
  usersRef.current = users;
  const [messages, setMessages] = useState<DisplayChatItem[]>([]);
  const messagesRef = useRef<DisplayChatItem[]>([]);
  messagesRef.current = messages;
  const [lastError, setLastError] = useState<string | null>(null);
  /** Report delivery outcome — driven ONLY by the server's report_ack or an
   * error; the UI must not fake success (the old local toast lied). */
  const [reportOutcome, setReportOutcome] = useState<
    | { status: "pending" }
    | { status: "sent" }
    | { status: "failed" }
    | null
  >(null);
  const reportPendingRef = useRef(false);
  const reportTimerRef = useRef<number | null>(null);
  const [profileSyncing, setProfileSyncing] = useState(false);
  const [roomName] = useState(() => getRoomName());
  const [mode, setModeState] = useState<RoomMode>("watch");
  const [cards, setCards] = useState<FlashCard[]>(() =>
    DEFAULT_FLASHCARDS.map((c) => ({ ...c })),
  );
  const [views, setViews] = useState<Record<string, FlashViewState>>({});
  const [media, setMediaState] = useState<RoomMedia>({ kind: "default" });
  /**
   * Estimated client↔server wall-clock offset (ms). The shared playhead is
   * anchored in SERVER time (`MediaPlayback.at` is set by the DO), but each
   * client extrapolates it with its own Date.now() — device clock skew
   * shows up as a systematic sync lag/lead. Every room_state carries the
   * server's clock; offset = serverTime − localNow at receipt.
   */
  const [clockOffsetMs, setClockOffsetMs] = useState(0);
  const [flashLock, setFlashLock] = useState(false);
  const [rateLimitUntil, setRateLimitUntil] = useState(0);
  const [chatLocked, setChatLocked] = useState(false);
  const mediaRef = useRef(media);
  const modeRef = useRef(mode);
  const cardsRef = useRef(cards);
  const viewsRef = useRef(views);
  const flashLockRef = useRef(flashLock);
  const ownerIdRef = useRef(ownerId);
  const ownerProRef = useRef(ownerPro);
  const mutedIdsRef = useRef(mutedIds);
  const bannedUsersRef = useRef(bannedUsers);
  mediaRef.current = media;
  modeRef.current = mode;
  cardsRef.current = cards;
  viewsRef.current = views;
  flashLockRef.current = flashLock;
  ownerIdRef.current = ownerId;
  ownerProRef.current = ownerPro;
  mutedIdsRef.current = mutedIds;
  bannedUsersRef.current = bannedUsers;
  /** >0 while our optimistic flip hasn't been confirmed by room_state. */
  const pendingFlipRef = useRef(0);

  meRef.current = me;

  const myView = useMemo((): FlashViewState => {
    if (!me) {
      return { cardIndex: 0, flipped: false, followingUserId: null };
    }
    return (
      views[me.id] ?? {
        cardIndex: 0,
        flipped: false,
        followingUserId: null,
      }
    );
  }, [views, me]);

  const cardIndex = myView.cardIndex;
  const flipped = myView.flipped;
  const followingUserId = myView.followingUserId;

  const pushMessage = useCallback((item: DisplayChatItem) => {
    setMessages((prev) => {
      // Guard against history/live races duplicating the same chat row.
      if (item.type === "chat" && item.id) {
        if (prev.some((m) => m.type === "chat" && m.id === item.id)) {
          return prev;
        }
      }
      const next = [...prev, item];
      if (next.length > config.maxChatMessages) {
        return next.slice(next.length - config.maxChatMessages);
      }
      return next;
    });
  }, []);

  const clearProfileTimeout = useCallback(() => {
    if (profileTimeoutRef.current !== null) {
      clearTimeout(profileTimeoutRef.current);
      profileTimeoutRef.current = null;
    }
  }, []);

  const beginProfileSync = useCallback(() => {
    setProfileSyncing(true);
    clearProfileTimeout();
    const deadline =
      statusRef.current === "connected"
        ? PROFILE_SYNC_TIMEOUT_MS
        : 30_000;
    profileTimeoutRef.current = setTimeout(() => {
      profileTimeoutRef.current = null;
      setProfileSyncing(false);
      setLastError("Couldn't update profile — try again");
    }, deadline);
  }, [clearProfileTimeout]);

  const endProfileSync = useCallback(() => {
    clearProfileTimeout();
    setProfileSyncing(false);
  }, [clearProfileTimeout]);

  /** Drop the oldest pending item that matches this text (any status). */
  const ackPendingChat = useCallback((username: string, text: string) => {
    setMessages((prev) => {
      const idx = prev.findIndex(
        (m) =>
          m.type === "pending" &&
          m.username === username &&
          m.text === text,
      );
      if (idx < 0) return prev;
      const next = prev.slice();
      next.splice(idx, 1);
      return next;
    });
  }, []);

  /** Drop the oldest in-flight pending bubble (e.g. rate-limited send). */
  const dropOldestPending = useCallback(() => {
    setMessages((prev) => {
      const idx = prev.findIndex(
        (m) => m.type === "pending" && m.status !== "failed",
      );
      if (idx < 0) return prev;
      const next = prev.slice();
      next.splice(idx, 1);
      return next;
    });
  }, []);

  /** Mark oldest un-acked pending as failed (rate-limit / hard error). */
  const failOldestPending = useCallback(() => {
    setMessages((prev) => {
      const idx = prev.findIndex(
        (m) => m.type === "pending" && m.status !== "failed",
      );
      if (idx < 0) return prev;
      const next = prev.slice();
      const item = next[idx] as PendingChatMessage;
      next[idx] = { ...item, status: "failed" };
      return next;
    });
  }, []);

  const handleServerMessage = useCallback(
    (msg: ServerMessage) => {
      switch (msg.type) {
        case "connected":
          break;
        case "identity":
          setMe(msg.user);
          saveIdentity(msg.user);
          usernameRef.current = msg.user.username;
          connRef.current?.setIdentity({
            userId: msg.user.id,
            username: msg.user.username,
            color: msg.user.color,
            avatarSeed: msg.user.avatarSeed,
          });
          break;
        case "history":
          if (msg.reset) {
            // Operator wipe: replace the panel, don't merge with leftovers.
            setMessages(msg.messages);
            break;
          }
          setMessages((prev) => {
            const pending = prev.filter((m) => m.type === "pending");
            const nonPending = prev.filter((m) => m.type !== "pending");
            const seenIds = new Set<string>();
            const seenKeys = new Set<string>();
            for (const m of nonPending) {
              if (m.type === "chat") {
                if (m.id) seenIds.add(m.id);
                seenKeys.add(`${m.timestamp}|${m.username}|${m.text}`);
              } else if (m.type === "system") {
                seenKeys.add(`${(m as { timestamp?: number }).timestamp ?? ""}|${m.text}`);
              } else if (m.type === "whisper") {
                // Whispers are persisted like chat — a live broadcast can
                // arrive, then a reconnect replays it from history. Without
                // dedupe here, the same whisper renders twice.
                if (m.id) seenIds.add(m.id);
                seenKeys.add(`w|${m.timestamp}|${m.username}|${m.targetName}|${m.text}`);
              } else if (m.type === "moderation") {
                seenKeys.add(`mod|${m.timestamp}|${m.action}|${m.actorName}|${m.targetNames.join(",")}`);
              }
            }
            const restored: typeof msg.messages = [];
            for (const raw of msg.messages) {
              if (raw.type === "chat") {
                const m = {
                  ...raw,
                  id: raw.id || `legacy-${raw.timestamp}-${raw.username}`,
                  reactions: raw.reactions ?? {},
                };
                const key = `${m.timestamp}|${m.username}|${m.text}`;
                if (seenIds.has(m.id) || seenKeys.has(key)) continue;
                seenIds.add(m.id);
                seenKeys.add(key);
                restored.push(m);
                continue;
              }
              if (raw.type === "system") {
                const key = `${raw.timestamp}|${raw.text}`;
                if (seenKeys.has(key)) continue;
                seenKeys.add(key);
                restored.push(raw);
                continue;
              }
              if (raw.type === "whisper") {
                const key = `w|${raw.timestamp}|${raw.username}|${raw.targetName}|${raw.text}`;
                if ((raw.id && seenIds.has(raw.id)) || seenKeys.has(key)) continue;
                if (raw.id) seenIds.add(raw.id);
                seenKeys.add(key);
                restored.push(raw);
                continue;
              }
              if (raw.type === "moderation") {
                const key = `mod|${raw.timestamp}|${raw.action}|${raw.actorName}|${raw.targetNames.join(",")}`;
                if (seenKeys.has(key)) continue;
                seenKeys.add(key);
                restored.push(raw);
              }
            }
            // Ack any pending that already appear in history (e.g. after rejoin).
            const stillPending = pending.filter((p) => {
              if (p.type !== "pending") return true;
              return !msg.messages.some(
                (h) => h.type === "chat" && h.username === p.username && h.text === p.text,
              );
            });
            // Merge restored history with what's already on screen in
            // chronological order. Appending restored items to the front used
            // to scramble the timeline once the ring buffer was full.
            const combined = [...restored, ...nonPending].sort(
              (a, b) => messageTime(a) - messageTime(b),
            );
            const next = [...combined, ...stillPending];
            return next.length > config.maxChatMessages
              ? next.slice(next.length - config.maxChatMessages)
              : next;
          });
          break;
        case "user_updated":
          setMe(msg.user);
          saveIdentity(msg.user);
          usernameRef.current = msg.user.username;
          connRef.current?.setIdentity({
            userId: msg.user.id,
            username: msg.user.username,
            color: msg.user.color,
            avatarSeed: msg.user.avatarSeed,
          });
          setUsers((prev) =>
            prev.map((u) => (u.id === msg.user.id ? msg.user : u)),
          );
          prevMeRef.current = null;
          endProfileSync();
          setLastError(null);
          break;
        case "chat": {
          const chat = msg as ChatBroadcastMessage;
          // Prefer ack of our optimistic bubble; always show the real broadcast.
          ackPendingChat(chat.username, chat.text);
          pushMessage(chat);
          // Mention ping — flash tab title if someone @'d us.
          const self = meRef.current;
          if (self && chat.username !== self.username) {
            const mention = new RegExp(
              `@${escapeRegExp(self.username)}\\b`,
              "i",
            );
            if (mention.test(chat.text)) {
              flashMentionTitle(self.username);
            }
          }
          break;
        }
        case "reaction":
          setMessages((prev) =>
            prev.map((m) =>
              m.type === "chat" && m.id === msg.messageId
                ? { ...m, reactions: msg.reactions }
                : m,
            ),
          );
          break;
        case "room_state": {
          // Server clock stamp → offset estimate (skewed device clocks are
          // the #1 cause of "their video lags behind mine"). Compute before
          // any early-break so the media-only scrub path updates it too.
          if (typeof msg.serverTime === "number") {
            const offset = msg.serverTime - Date.now();
            // Accept real changes only — avoids re-render churn when the
            // stamp just ticks forward with our own clock.
            setClockOffsetMs((prev) =>
              Math.abs(prev - offset) < 5 ? prev : offset,
            );
          }
          const nextViews = normalizeViews(msg.views);
          const nextCards = Array.isArray(msg.cards) ? msg.cards : cardsRef.current;
          const nextMedia = msg.media ?? mediaRef.current;

          // Frequent YouTube scrub sync — skip unrelated state churn.
          // Never take this path while a flip is in flight: the confirming
          // room_state often matches our optimistic views already, and
          // skipping would leave pendingFlip stuck (blocking further flips).
          if (
            pendingFlipRef.current === 0 &&
            msg.media &&
            isYoutubePlaybackOnlyRoomState(
              mediaRef.current,
              nextMedia,
              modeRef.current,
              msg.mode,
              cardsRef.current,
              nextCards,
              viewsRef.current,
              nextViews,
              flashLockRef.current,
              msg.flashLock?.locked ?? false,
              ownerIdRef.current,
              msg.ownerId ?? null,
              ownerProRef.current,
              msg.ownerPro ?? false,
              mutedIdsRef.current,
              msg.mutedIds ?? [],
              bannedUsersRef.current,
              msg.bannedUsers ?? [],
            )
          ) {
            setMediaState((prev) =>
              mediaEqual(prev, nextMedia) ? prev : nextMedia,
            );
            break;
          }

          // Solo page load: server may still say nerd because a reconnect
          // overlapped the old socket (or mode was persisted). Don't inherit it.
          // Use views count from this payload — usersRef is stale until next render.
          const boot = bootModeRef.current;
          const viewCount = Object.keys(
            msg.views && typeof msg.views === "object" ? msg.views : {},
          ).length;
          if (!boot.handled && msg.mode === "nerd" && viewCount <= 1) {
            boot.handled = true;
            setModeState("watch");
            queueMicrotask(() => {
              connRef.current?.send({ type: "set_mode", mode: "watch" });
            });
          } else {
            boot.handled = true;
            setModeState((prev) => (prev === msg.mode ? prev : msg.mode));
          }
          setCards((prev) =>
            Array.isArray(msg.cards) && flashCardsEqual(prev, msg.cards)
              ? prev
              : Array.isArray(msg.cards)
                ? msg.cards
                : prev,
          );
          // Guard: older workers (or bad payloads) may omit `views` — never crash.
          setViews((prev) => {
            let merged = nextViews;
            // Hold optimistic flip until the server agrees — otherwise a
            // concurrent room_state (someone else navigating) can briefly
            // snap the card face back and flicker.
            if (pendingFlipRef.current > 0 && meRef.current) {
              const id = meRef.current.id;
              const local = prev[id];
              const remote = nextViews[id];
              if (local && remote && local.flipped !== remote.flipped) {
                merged = {
                  ...nextViews,
                  [id]: { ...remote, flipped: local.flipped },
                };
              } else {
                pendingFlipRef.current = 0;
              }
            } else {
              pendingFlipRef.current = 0;
            }
            return flashViewsEqual(prev, merged) ? prev : merged;
          });
          setFlashLock(msg.flashLock?.locked ?? false);
          if (typeof msg.ownerId === "string") setOwnerId(msg.ownerId);
          if (typeof msg.ownerPro === "boolean") setOwnerPro(msg.ownerPro);
          if (Array.isArray(msg.bannedUsers)) setBannedUsers(msg.bannedUsers);
          if (Array.isArray(msg.mutedIds)) setMutedIds(msg.mutedIds);
          if (msg.media) {
            setMediaState((prev) =>
              mediaEqual(prev, msg.media) ? prev : msg.media,
            );
          }
          break;
        }
        case "system":
          pushMessage(msg);
          break;
        case "moderation":
        case "whisper":
          pushMessage(msg);
          break;
        case "muted":
          setMuted(msg.muted);
          break;
        case "poll":
          setPoll(msg.poll && !msg.poll.closed ? msg.poll : null);
          break;
        case "vote_state":
          setVote(msg.vote && !msg.vote.closed ? msg.vote : null);
          break;
        case "presence":
          // Ignore empty presence blips while we still have an identity —
          // reconnect/wake can briefly broadcast [] before the real roster,
          // which made the theater collapse then "reappear".
          if (msg.users.length === 0 && meRef.current) {
            break;
          }
          setUsers((prev) =>
            usersShallowEqual(prev, msg.users) ? prev : msg.users,
          );
          // Stale local `me` (old user id after a silent session rebuild) shows
          // up as a phantom second seat. Rejoin to adopt the live identity.
          {
            const self = meRef.current;
            if (
              self &&
              msg.users.length > 0 &&
              !msg.users.some((u) => u.id === self.id)
            ) {
              const now = Date.now();
              if (now - lastRejoinRef.current > 10_000) {
                lastRejoinRef.current = now;
                connRef.current?.rejoin();
              }
            }
          }
          break;
        case "error":
          // A failed report must be surfaced (the target may have left, or
          // the action limiter kicked in). Session-expired never reaches this
          // handler — the connection layer self-heals and retries it.
          if (reportPendingRef.current) {
            reportPendingRef.current = false;
            if (reportTimerRef.current) window.clearTimeout(reportTimerRef.current);
            setReportOutcome({ status: "failed" });
            window.setTimeout(() => setReportOutcome(null), 4_000);
          }
          if (msg.waitMs) {
            const until = Date.now() + Math.max(msg.waitMs, 0);
            if (until > rateLimitUntilRef.current) {
              rateLimitUntilRef.current = until;
              setRateLimitUntil(until);
            }
            dropOldestPending();
          } else {
            // Session-expired is handled (and swallowed) in the connection layer.
            // Other errors: fail in-flight chat + surface for profile UI.
            failOldestPending();
            pushMessage({ type: "system", text: `Error: ${msg.text}`, timestamp: Date.now() });
            setLastError(msg.text);
            endProfileSync();
            // If a reroll was in-flight and failed, restore the previous identity
            // so the local state doesn't stay permanently desynced from the server.
            if (prevMeRef.current) {
              setMe(prevMeRef.current);
              saveIdentity(prevMeRef.current);
              connRef.current?.setIdentity({
                userId: prevMeRef.current.id,
                username: prevMeRef.current.username,
                color: prevMeRef.current.color,
                avatarSeed: prevMeRef.current.avatarSeed,
              });
              prevMeRef.current = null;
            }
          }
          break;
        case "pong":
          break;
        case "report_ack":
          // Server recorded the report — only now may the UI claim success.
          reportPendingRef.current = false;
          if (reportTimerRef.current) window.clearTimeout(reportTimerRef.current);
          setReportOutcome({ status: "sent" });
          window.setTimeout(() => setReportOutcome(null), 3_000);
          break;
      }
    },
    [pushMessage, ackPendingChat, dropOldestPending, failOldestPending, endProfileSync],
  );

  useEffect(() => {
    if (!rateLimitUntil || rateLimitUntil <= Date.now()) {
      setChatLocked(false);
      if (rateLimitUntil) {
        rateLimitUntilRef.current = 0;
        setRateLimitUntil(0);
      }
      return;
    }

    setChatLocked(true);
    const id = window.setInterval(() => {
      if (Date.now() >= rateLimitUntil) {
        rateLimitUntilRef.current = 0;
        setRateLimitUntil(0);
        setChatLocked(false);
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [rateLimitUntil]);

  useEffect(() => {
    return () => {
      connRef.current?.disconnect();
      connRef.current = null;
      clearProfileTimeout();
      if (sessionNoticeRef.current) clearTimeout(sessionNoticeRef.current);
      if (disconnectNoticeRef.current) clearTimeout(disconnectNoticeRef.current);
    };
  }, [clearProfileTimeout]);

  // Time out stuck "sending" bubbles. Use a longer window while reconnecting
  // since the outbox will flush the message once the connection is back.
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      const deadline =
        statusRef.current === "connected"
          ? CHAT_DELIVERY_TIMEOUT_MS
          : 30_000;
      setMessages((prev) => {
        let changed = false;
        const next = prev.map((m) => {
          if (m.type !== "pending") return m;
          if (m.status === "optimistic" && now - m.sentAt >= SHOW_SENDING_AFTER_MS) {
            changed = true;
            return { ...m, status: "sending" as const };
          }
          if (m.status === "sending" && now - m.sentAt >= deadline) {
            changed = true;
            return { ...m, status: "failed" as const };
          }
          return m;
        });
        return changed ? next : prev;
      });
    }, 1_000);
    return () => clearInterval(timer);
  }, []);

  const join = useCallback(
    async (username: string, password?: string) => {
      const trimmed = username.trim().slice(0, config.maxUsernameLength);
      if (!trimmed) return;

      usernameRef.current = trimmed;
      // A fresh join clears any prior kicked/banned/capacity terminal state.
      setKicked(null);
      setCapacity(false);
      setJoinBlocked(null);
      try {
        localStorage.setItem(config.usernameStorageKey, trimmed);
      } catch {
        /* localStorage unavailable or full — carry on */
      }

      if (connRef.current) {
        connRef.current.disconnect();
      }

      const saved = loadIdentity();
      const sessionId = await getSessionId();
      const conn = new YayConnection(
        wsUrlForRoom(getRoomName()),
        sessionId,
        trimmed,
        {
          // Join form name is authoritative; id/color/avatar come from saved identity.
          userId: saved?.id,
          username: trimmed,
          color: saved?.color,
          avatarSeed: saved?.avatarSeed,
        },
        password?.trim() ? password.trim() : null,
      );
      connRef.current = conn;

      conn.onStatus(setStatus);
      conn.onReady(setSessionReady);
      conn.onMessage(handleServerMessage);
      conn.onKicked(setKicked);
      conn.onCapacity(() => setCapacity(true));
      conn.onPasswordFail(setJoinBlocked);
      conn.connect();
    },
    [handleServerMessage],
  );

  /** Drop the capacity screen back to the join overlay (gate reopened). */
  const clearCapacity = useCallback(() => setCapacity(false), []);
  const clearJoinBlocked = useCallback(() => setJoinBlocked(null), []);

  const sendChat = useCallback((
    text: string,
    replyToId?: string,
    replyHint?: ChatReplyRef,
  ): boolean => {
    if (Date.now() < rateLimitUntilRef.current) return false;
    const trimmed = text.trim().slice(0, config.maxMessageLength);
    if (!trimmed || !connRef.current) return false;

    if (/^\/unlock$/i.test(trimmed)) {
      return connRef.current.send({ type: "set_password", password: "" });
    }
    const lock = trimmed.match(/^\/lock\s+(.+)$/i);
    if (lock) {
      return connRef.current.send({
        type: "set_password",
        password: lock[1]!.trim(),
      });
    }

    const self = meRef.current;
    if (!self) return false;

    let replyTo: ChatReplyRef | undefined;
    if (replyToId) {
      const parent = messagesRef.current.find(
        (m) => m.type === "chat" && m.id === replyToId,
      );
      if (parent && parent.type === "chat") {
        replyTo = {
          id: parent.id,
          username: parent.username,
          text: parent.text.slice(0, 120),
        };
      } else if (replyHint) {
        replyTo = replyHint;
      }
    }

    const clientId = crypto.randomUUID();
    pushMessage({
      type: "pending",
      clientId,
      username: self.username,
      color: self.color,
      text: trimmed,
      status: "optimistic",
      sentAt: Date.now(),
      ...(replyTo ? { replyTo } : {}),
    });

    const liveReply = replyToId && !replyToId.startsWith("sc-");
    return connRef.current.send({
      type: "chat",
      text: trimmed,
      ...(liveReply ? { replyToId } : {}),
    });
  }, [pushMessage]);

  const retryChat = useCallback((clientId: string): boolean => {
    if (Date.now() < rateLimitUntilRef.current) return false;
    if (!connRef.current) return false;
    let text: string | null = null;
    let replyToId: string | undefined;

    setMessages((prev) =>
      prev.map((m) => {
        if (m.type === "pending" && m.clientId === clientId) {
          text = m.text;
          replyToId = m.replyTo?.id;
          return { ...m, status: "sending" as const, sentAt: Date.now() };
        }
        return m;
      }),
    );

    if (!text) return false;
    return connRef.current.send({
      type: "chat",
      text,
      ...(replyToId ? { replyToId } : {}),
    });
  }, []);

  const react = useCallback((messageId: string, emoji: string): boolean => {
    if (!connRef.current || !messageId || !emoji) return false;
    return connRef.current.send({ type: "react", messageId, emoji });
  }, []);

  const rename = useCallback((username: string): boolean => {
    const trimmed = username.trim().slice(0, config.maxUsernameLength);
    if (!trimmed || !connRef.current) return false;
    usernameRef.current = trimmed;
    try {
      localStorage.setItem(config.usernameStorageKey, trimmed);
    } catch {
      /* localStorage unavailable or full — carry on */
    }
    connRef.current.setUsername(trimmed);
    beginProfileSync();
    return connRef.current.send({ type: "rename", username: trimmed });
  }, [beginProfileSync]);

  const reroll = useCallback((): boolean => {
    if (!connRef.current || !meRef.current) return false;
    const self = meRef.current;
    const newSeed = crypto.randomUUID();
    // Keep connection identity in sync so a mid-flight rejoin still rebuilds
    // with the avatar the user just picked (not the previous seed).
    connRef.current.setIdentity({
      userId: self.id,
      username: self.username,
      color: self.color,
      avatarSeed: newSeed,
    });
    beginProfileSync();
    const ok = connRef.current.send({ type: "reroll", avatarSeed: newSeed });
    if (ok) {
      prevMeRef.current = self;
      setMe((prev) => (prev ? { ...prev, avatarSeed: newSeed } : prev));
      saveIdentity({ ...self, avatarSeed: newSeed });
    } else {
      // Send failed immediately — revert the connection identity so a
      // subsequent reconnect rebuilds with the current server-approved seed.
      connRef.current.setIdentity({
        userId: self.id,
        username: self.username,
        color: self.color,
        avatarSeed: self.avatarSeed,
      });
      endProfileSync();
    }
    return ok;
  }, [beginProfileSync, endProfileSync]);

  const leave = useCallback(() => {
    usernameRef.current = null;
    connRef.current?.leave();
    connRef.current = null;
    setMe(null);
    setUsers([]);
    setStatus("idle");
    setSessionReady(false);
    setProfileSyncing(false);
    clearProfileTimeout();
  }, [clearProfileTimeout]);

  const clearError = useCallback(() => setLastError(null), []);

  // Remove any stale "Reconnecting…" from the chat.
  const clearReconnecting = useCallback(() => {
    setMessages((prev) => prev.filter((m) => !(m.type === "system" && m.text === "Reconnecting…")));
  }, []);

  // Linger disconnect notice (quiet for short blips; silent when tab is hidden).
  useEffect(() => {
    if (status === "disconnected" && usernameRef.current) {
      if (disconnectNoticeRef.current === null) {
        const tabWasHidden = typeof document !== "undefined" && document.visibilityState === "hidden";
        if (tabWasHidden) return;
        disconnectNoticeRef.current = setTimeout(() => {
          disconnectNoticeRef.current = null;
          pushMessage({ type: "system", text: "Reconnecting…", timestamp: Date.now() });
        }, DISCONNECT_NOTICE_MS);
      }
    } else {
      if (disconnectNoticeRef.current !== null) {
        clearTimeout(disconnectNoticeRef.current);
        disconnectNoticeRef.current = null;
      }
      // Connected again — sweep any stale "Reconnecting…" messages.
      if (status === "connected") {
        clearReconnecting();
      }
    }
    return () => {
      if (disconnectNoticeRef.current !== null && status !== "disconnected") {
        clearTimeout(disconnectNoticeRef.current);
        disconnectNoticeRef.current = null;
      }
    };
  }, [status, pushMessage, clearReconnecting]);

  // Session rejoin notice (socket open but identity not confirmed yet).
  useEffect(() => {
    if (sessionNoticeRef.current) {
      clearTimeout(sessionNoticeRef.current);
      sessionNoticeRef.current = null;
    }
    if (
      status === "connected" &&
      !sessionReady &&
      usernameRef.current
    ) {
      sessionNoticeRef.current = setTimeout(() => {
        sessionNoticeRef.current = null;
        pushMessage({ type: "system", text: "Restoring session…", timestamp: Date.now() });
      }, SESSION_SYNC_NOTICE_MS);
    }
    return () => {
      if (sessionNoticeRef.current) {
        clearTimeout(sessionNoticeRef.current);
        sessionNoticeRef.current = null;
      }
    };
  }, [status, sessionReady, pushMessage]);

  // ── mock chat stream ──────────────────────────────────────────

  useEffect(() => {
    if (!MOCK_ENABLED) return;

    let timer: ReturnType<typeof setInterval>;
    let first = true;

    function tick() {
      setMessages((prev) => {
        const next = [...prev, randomMockMessage()];
        return next.slice(-config.maxChatMessages);
      });

      if (first) {
        first = false;
        for (let i = 0; i < 10; i++) {
          setMessages((prev) => {
            const next = [...prev, randomMockMessage()];
            return next.slice(-config.maxChatMessages);
          });
        }
      }
    }

    tick();
    timer = setInterval(() => {
      const delay = 400 + Math.random() * 800;
      clearInterval(timer);
      timer = setInterval(tick, delay);
    }, 400);

    return () => clearInterval(timer);
  }, []);

  const setMode = useCallback((next: RoomMode): boolean => {
    if (!connRef.current) return false;
    setModeState(next);
    return connRef.current.send({ type: "set_mode", mode: next });
  }, []);

  const flashFlip = useCallback((): boolean => {
    if (!connRef.current) return false;
    if (followingUserId) return false;
    // Always toggle locally — don't block on a prior pending confirm.
    // (YouTube playback-only room_state used to skip clearing pendingFlip.)
    pendingFlipRef.current = 1;
    setViews((prev) => {
      if (!meRef.current) return prev;
      const cur = prev[meRef.current.id] ?? {
        cardIndex: 0,
        flipped: false,
        followingUserId: null,
      };
      return {
        ...prev,
        [meRef.current.id]: { ...cur, flipped: !cur.flipped },
      };
    });
    return connRef.current.send({ type: "flash_flip" });
  }, [followingUserId]);

  const flashNav = useCallback((delta: number): boolean => {
    if (!connRef.current || followingUserId) return false;
    return connRef.current.send({ type: "flash_nav", delta });
  }, [followingUserId]);

  const flashAdd = useCallback((term: string, definition: string): boolean => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "flash_add", term, definition });
  }, []);

  const flashImport = useCallback(
    (cards: Array<{ term: string; definition: string }>): boolean => {
      if (!connRef.current || cards.length === 0) return false;
      return connRef.current.send({ type: "flash_import", cards });
    },
    [],
  );

  const flashUpdate = useCallback(
    (id: string, term: string, definition: string): boolean => {
      if (!connRef.current) return false;
      return connRef.current.send({ type: "flash_update", id, term, definition });
    },
    [],
  );

  const flashDelete = useCallback((id: string): boolean => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "flash_delete", id });
  }, []);

  const flashFollow = useCallback((targetUserId: string | null): boolean => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "flash_follow", targetUserId });
  }, []);

  const setMedia = useCallback((youtube: string): boolean => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "set_media", youtube });
  }, []);

  const syncMedia = useCallback(
    (sync: { playing?: boolean; positionSec?: number }): boolean => {
      if (!connRef.current) return false;
      return connRef.current.send({ type: "media_sync", ...sync });
    },
    [],
  );

  const report = useCallback((targetUserId: string, reason: string): boolean => {
    if (!connRef.current || !targetUserId || !reason) return false;
    reportPendingRef.current = true;
    setReportOutcome({ status: "pending" });
    if (reportTimerRef.current) window.clearTimeout(reportTimerRef.current);
    // Safety net: if no ack or error arrives (dead socket that never
    // recovers), stop showing "sending…" and surface a failure.
    reportTimerRef.current = window.setTimeout(() => {
      if (reportPendingRef.current) {
        reportPendingRef.current = false;
        setReportOutcome({ status: "failed" });
        window.setTimeout(() => setReportOutcome(null), 4_000);
      }
    }, 15_000);
    return connRef.current.send({ type: "report", targetUserId, reason });
  }, []);

  const flashSetLock = useCallback((locked: boolean): boolean => {
    if (!connRef.current) return false;
    return connRef.current.send({ type: "flash_lock", locked });
  }, []);

  const sendCommand = useCallback(
    (action: ModerationAction, targetIds: string[]): boolean => {
      if (!connRef.current || targetIds.length === 0) return false;
      return connRef.current.send({ type: "command", action, targetIds });
    },
    [],
  );

  const sendWhisper = useCallback(
    (targetUserId: string, text: string): boolean => {
      const trimmed = text.trim().slice(0, config.maxMessageLength);
      if (!connRef.current || !targetUserId || !trimmed) return false;
      return connRef.current.send({
        type: "whisper",
        targetUserId,
        text: trimmed,
      });
    },
    [],
  );

  const sendPollCreate = useCallback(
    (title: string, options: string[]): boolean => {
      if (!connRef.current || !title.trim() || options.length < 2) return false;
      return connRef.current.send({
        type: "poll_create",
        title: title.trim().slice(0, 80),
        options: options
          .map((o) => o.trim().slice(0, 60))
          .filter(Boolean)
          .slice(0, 8),
      });
    },
    [],
  );

  const sendPollVote = useCallback(
    (pollId: string, optionIndex: number): boolean => {
      if (!connRef.current || !pollId) return false;
      return connRef.current.send({ type: "poll_vote", pollId, optionIndex });
    },
    [],
  );

  const sendPollClose = useCallback(
    (pollId: string): boolean => {
      if (!connRef.current || !pollId) return false;
      return connRef.current.send({ type: "poll_close", pollId });
    },
    [],
  );

  const sendVoteCast = useCallback(
    (voteId: string, approve: boolean): boolean => {
      if (!connRef.current || !voteId) return false;
      return connRef.current.send({ type: "vote_cast", voteId, approve });
    },
    [],
  );

  const mergedUsers = MOCK_ENABLED
    ? [...MOCK_USERS, ...(me ? [me] : [])]
    : users;

  const displayMessages = useMemo(() => messages, [messages]);

  return {
    status,
    sessionReady,
    me,
    users: mergedUsers,
    viewerCount: MOCK_ENABLED ? mergedUsers.length : users.length,
    messages: displayMessages,
    lastError,
    profileSyncing,
    roomName,
    mode,
    cards,
    cardIndex,
    flipped,
    followingUserId,
    media,
    clockOffsetMs,
    flashLock,
    flashSetLock,
    ownerId,
    ownerPro,
    bannedUsers,
    isOwner: !!me && me.id !== "" && me.id === ownerId,
    mutedIds,
    muted,
    poll,
    join,
    sendChat,
    retryChat,
    chatLocked,
    rateLimitUntil,
    react,
    rename,
    reroll,
    leave,
    clearError,
    setMode,
    flashFlip,
    flashNav,
    flashAdd,
    flashImport,
    flashUpdate,
    flashDelete,
    flashFollow,
    setMedia,
    syncMedia,
    report,
    reportOutcome,
    sendCommand,
    sendWhisper,
    sendPollCreate,
    sendPollVote,
    sendPollClose,
    vote,
    sendVoteCast,
    kicked,
    capacity,
    clearCapacity,
    joinBlocked,
    clearJoinBlocked,
  };
}
